import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { Patient } from '@/types/patients';
import { PatientPhotoEditor } from './PatientPhotoEditor';
import { ageFromDob, formatIsoDate, formatShortDate, sexLabelKey } from './patientDisplay';

interface PatientProfileCardProps {
  patient: Patient;
}

/**
 * The registry's demographic profile: a large photo and the patient's PLAINTEXT
 * demographics. The photo is editable in place (tap to enlarge, camera badge to
 * take / choose / remove) via the same `PatientPhotoEditor` the doctor's chart
 * uses — photo endpoints are open to all clinic staff. Only the unencrypted,
 * searchable columns are shown (name, MRN, sex, DOB, phone, photo, registered) —
 * the encrypted PHI extras (national ID, address, occupation, blood group) are
 * deliberately omitted, as is the clinical record (allergies, chronic
 * conditions, consultations, obstetric history), which lives behind the
 * doctor-only Patients page. A field with no value renders an em-dash so the
 * sheet reads as complete, not broken.
 */
export function PatientProfileCard({ patient }: PatientProfileCardProps) {
  const { t } = useTranslation();

  const age = ageFromDob(patient.dateOfBirth);
  const sex = t(sexLabelKey(patient.sex));
  const dob = formatIsoDate(patient.dateOfBirth);

  // Date of birth reads "05 Jun 1990 · 36 yrs" when both are known; age alone
  // (estimated records) or DOB alone are both valid partial states.
  const dobValue =
    dob && age != null
      ? `${dob} · ${age} ${t('patients.yrs')}`
      : dob || (age != null ? `${age} ${t('patients.yrs')}` : null);

  return (
    <div className="flex flex-col gap-6">
      {/* Hero: editable photo (enlarge + take/choose/remove) + name + glance line */}
      <div className="flex items-start gap-4">
        <PatientPhotoEditor
          patient={patient}
          size={104}
          className="shrink-0"
          avatarClassName="h-24 w-24 ring-1 ring-line md:h-28 md:w-28"
          badgeClassName="-bottom-0.5 -right-0.5 h-8 w-8"
          badgeIconSize={16}
        />

        <div className="min-w-0 flex-1 pt-1">
          <h2 className="text-[22px] font-bold leading-tight tracking-[-0.02em] md:text-[26px]">
            {patient.fullName}
          </h2>
          <div className="mt-1.5 text-[13.5px] text-muted">
            {[age != null ? `${age} ${t('patients.yrs')}` : null, sex].filter(Boolean).join(' · ')}
          </div>
          <div className="mt-1 font-mono text-[12.5px] text-muted">
            {t('patients.mrnLabel')} {patient.mrn}
          </div>
        </div>
      </div>

      {/* Demographic detail sheet — hairline grid, two columns on wider panes.
          Only plaintext columns: the encrypted PHI extras (NIC, address,
          occupation, blood group) are intentionally not shown in the registry. */}
      <dl className="grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-line bg-line sm:grid-cols-2">
        <ProfileField label={t('registry.profile.dateOfBirth')} value={dobValue} />
        <ProfileField label={t('registry.profile.sex')} value={sex} />
        <ProfileField label={t('registry.profile.phone')} value={patient.phone} mono />
        <ProfileField label={t('registry.profile.registered')} value={formatShortDate(patient.createdAt)} />
      </dl>
    </div>
  );
}

function ProfileField({
  label,
  value,
  mono,
}: {
  label: string;
  value: ReactNode;
  /** Monospace the value (phone numbers). */
  mono?: boolean;
}) {
  const empty = value == null || value === '';
  return (
    <div className="bg-paper px-4 py-3">
      <dt className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted">{label}</dt>
      <dd
        className={cn(
          'mt-0.5 break-words text-[14px] tracking-[-0.01em]',
          empty ? 'text-muted-2' : 'text-ink',
          mono && !empty && 'font-mono text-[13px]',
        )}
      >
        {empty ? '—' : value}
      </dd>
    </div>
  );
}
